import { PlannedDataRetention } from "./remaining-settings-sections";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  retentionChoices,
  type RetentionChoice,
  type ProjectExport,
} from "@zworks/console-contracts";
import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, LoadingState } from "@/components/states";
import {
  projectExportDownloadPath,
  projectExportsQuery,
  projectDeletionQuery,
  projectMembersQuery,
  clearProjectMemory,
  requestProjectDeletion,
  requestProjectExport,
  retentionQuery,
  settingsAccessLost,
  updateRetention,
} from "@/lib/api";
import { SettingsFailure } from "./settings-feedback";
import { settingsSelectClass } from "./general-settings-section";
import { formatExportDate } from "@/lib/format";

const retentionLabels: Record<RetentionChoice, string> = {
  "30-days": "30 days",
  "90-days": "90 days",
  "1-year": "1 year",
  forever: "Forever",
};

const retentionRank: Record<RetentionChoice, number> = {
  "30-days": 0,
  "90-days": 1,
  "1-year": 2,
  forever: 3,
};

const exportLabels: Record<ProjectExport["state"], string> = {
  building: "Building",
  ready: "Ready",
  expired: "Expired",
  failed: "Failed",
};
const exportBadgeVariants: Record<
  ProjectExport["state"],
  "info" | "success" | "warning" | "danger"
> = {
  building: "info",
  ready: "success",
  expired: "warning",
  failed: "danger",
};

type Confirmation =
  | "shorten"
  | "confirm-shortening"
  | "clear-memory"
  | "request-deletion"
  | "approve-deletion"
  | null;

export function DataControlsSection({
  projectId,
  isOwner,
  downloadExportId = null,
}: {
  projectId: string;
  isOwner: boolean;
  downloadExportId?: string | null;
}) {
  const queryClient = useQueryClient();
  const requestedDownload = useRef<string | null>(null);
  const retention = useQuery({ ...retentionQuery(projectId), enabled: isOwner });
  const exports = useQuery({
    ...projectExportsQuery(projectId),
    enabled: isOwner,
    refetchInterval: (query) =>
      query.state.data?.some((projectExport) => projectExport.state === "building") ? 5000 : false,
  });
  const members = useQuery({
    ...projectMembersQuery(projectId),
    enabled: isOwner,
  });
  const deletionStatus = useQuery({ ...projectDeletionQuery(projectId), enabled: isOwner });
  const [draft, setDraft] = useState<RetentionChoice | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const saveRetention = useMutation({
    mutationFn: ({ value, confirm }: { value: RetentionChoice; confirm: boolean }) =>
      updateRetention(projectId, { retention: value, confirm }),
    onSuccess: async (value) => {
      queryClient.setQueryData(retentionQuery(projectId).queryKey, value);
      setDraft(null);
      setConfirmation(null);
      setNotice(
        value.pendingRetention ? "Shorter retention requested." : "Retention period updated.",
      );
      await queryClient.invalidateQueries({ queryKey: retentionQuery(projectId).queryKey });
    },
  });
  const createExport = useMutation({
    mutationFn: () => requestProjectExport(projectId),
    onSuccess: async () => {
      setNotice("Export requested. A download link will be emailed when it is ready.");
      await queryClient.invalidateQueries({ queryKey: projectExportsQuery(projectId).queryKey });
    },
  });
  const deleteProject = useMutation({
    mutationFn: () => requestProjectDeletion(projectId),
    onSuccess: async (result) => {
      setConfirmation(null);
      setNotice(
        result.state === "approved"
          ? "Deletion approved. The project will be purged after its retention period."
          : "Deletion request sent. Another owner must approve it before the project is deleted.",
      );
      await queryClient.invalidateQueries({ queryKey: projectDeletionQuery(projectId).queryKey });
    },
  });
  const clearMemory = useMutation({
    mutationFn: () => clearProjectMemory(projectId),
    onSuccess: async ({ removedEntries }) => {
      setConfirmation(null);
      setNotice(`Project memory cleared. ${removedEntries} entries removed.`);
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "memory"] });
    },
  });

  useEffect(() => {
    const requested = exports.data?.find((item) => item.id === downloadExportId);
    if (
      !downloadExportId ||
      !requested ||
      requested.state !== "ready" ||
      requestedDownload.current === downloadExportId
    ) {
      return;
    }
    requestedDownload.current = downloadExportId;
    const link = document.createElement("a");
    link.href = projectExportDownloadPath(projectId, downloadExportId);
    link.download = "";
    link.click();
  }, [downloadExportId, exports.data, projectId]);

  const error =
    saveRetention.error ??
    createExport.error ??
    clearMemory.error ??
    deleteProject.error ??
    deletionStatus.error ??
    retention.error ??
    exports.error;
  if (!isOwner) {
    return (
      <section aria-label="Data controls" className="max-w-3xl">
        <SectionHeading>Data controls</SectionHeading>
        <p className="mt-1 text-sm text-muted-foreground">
          Only a project owner can manage retention, exports, or deletion.
        </p>
      </section>
    );
  }
  if (settingsAccessLost(error)) return <SettingsFailure error={error} />;
  if (retention.isPending || exports.isPending || members.isPending || deletionStatus.isPending)
    return <LoadingState label="Loading project data controls" rows={4} />;
  if (retention.isError)
    return <SettingsFailure error={retention.error} retry={() => void retention.refetch()} />;
  if (exports.isError)
    return <SettingsFailure error={exports.error} retry={() => void exports.refetch()} />;
  if (deletionStatus.isError)
    return (
      <SettingsFailure error={deletionStatus.error} retry={() => void deletionStatus.refetch()} />
    );
  const retentionData = retention.data;
  if (!retentionData)
    return <SettingsFailure error={new Error("Retention data is unavailable.")} />;
  if (
    members.isError &&
    (retentionData.pendingRetention || deletionStatus.data?.state === "requested")
  )
    return <SettingsFailure error={members.error} retry={() => void members.refetch()} />;

  const self = members.data?.find((member) => member.isSelf);
  const deletion = deletionStatus.data;
  const deletionRequestedBySelf =
    deletion?.state === "requested" && self !== undefined && deletion.requestedBy === self.userId;
  const canApproveDeletion =
    deletion?.state === "requested" &&
    deletion.requestedBy !== null &&
    self !== undefined &&
    deletion.requestedBy !== self.userId;
  const pending =
    saveRetention.isPending ||
    createExport.isPending ||
    clearMemory.isPending ||
    deleteProject.isPending;
  const currentValue = retentionData.pendingRetention ?? retentionData.retention;
  const selected = draft ?? currentValue;
  const pendingRequestedBySelf =
    retentionData.pendingRequestedBy !== null && retentionData.pendingRequestedBy === self?.userId;
  const hasPendingShortening = retentionData.pendingRetention !== null;

  function submitRetention() {
    if (pending || !draft || draft === retentionData.retention) return;
    const shortens = retentionRank[draft] < retentionRank[retentionData.retention];
    if (shortens) {
      setConfirmation("shorten");
    } else {
      saveRetention.mutate({ value: draft, confirm: false });
    }
  }

  function confirmShortening() {
    if (confirmation === "shorten" && draft) {
      saveRetention.mutate({ value: draft, confirm: false });
    } else if (
      confirmation === "confirm-shortening" &&
      retentionData.pendingRetention &&
      !pendingRequestedBySelf
    ) {
      saveRetention.mutate({ value: retentionData.pendingRetention, confirm: true });
    }
  }

  return (
    <section aria-label="Data controls" className="flex max-w-3xl flex-col gap-6">
      <div>
        <SectionHeading>Data controls</SectionHeading>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage how long project evidence is kept, export project records, or request deletion.
        </p>
      </div>

      <PlannedDataRetention />
      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}
      {error && <SettingsFailure error={error} />}

      <section
        aria-label="Project data retention"
        className="flex flex-col gap-3 rounded-[var(--radius-card)] border p-4"
      >
        <div>
          <h3 className="text-sm font-medium">Project data retention</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            This period applies to project evidence and Zot chat history. Current period:{" "}
            {retentionLabels[retentionData.retention]}.
          </p>
        </div>
        {hasPendingShortening ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-muted p-3">
            <p className="text-sm">
              {retentionLabels[retentionData.pendingRetention!]} is awaiting confirmation from
              another owner.
            </p>
            {pendingRequestedBySelf ? (
              <span className="text-sm text-muted-foreground">You requested this change.</span>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={pending || !self}
                onClick={() => setConfirmation("confirm-shortening")}
              >
                Confirm shorter retention
              </Button>
            )}
          </div>
        ) : (
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              submitRetention();
            }}
          >
            <label className="flex flex-1 flex-col gap-1.5 text-sm">
              Project data retention
              <select
                className={settingsSelectClass}
                value={selected}
                disabled={pending}
                onChange={(event) => {
                  setDraft(event.target.value as RetentionChoice);
                  setNotice(null);
                }}
              >
                {retentionChoices.map((choice) => (
                  <option key={choice} value={choice}>
                    {retentionLabels[choice]}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="submit"
              variant="outline"
              disabled={pending || selected === retentionData.retention}
            >
              {saveRetention.isPending ? "Saving…" : "Save retention"}
            </Button>
          </form>
        )}
      </section>

      <section
        aria-label="Project exports"
        className="flex flex-col gap-3 rounded-[var(--radius-card)] border p-4"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-medium">Project export</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Request a ZIP of runs, artefacts, audit history, and the credit ledger. Secrets are
              excluded.
            </p>
          </div>
          <Button type="button" disabled={pending} onClick={() => createExport.mutate()}>
            {createExport.isPending ? "Requesting…" : "Request export"}
          </Button>
        </div>
        {exports.data.length === 0 ? (
          <EmptyState
            title="No project exports"
            description="Requested exports will appear here, and the download link will be emailed when ready."
          />
        ) : (
          <ul className="divide-y rounded-md border" aria-label="Project exports">
            {exports.data.map((projectExport) => (
              <li
                key={projectExport.id}
                className="flex flex-wrap items-center justify-between gap-3 px-3 py-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant={exportBadgeVariants[projectExport.state]}>
                    {exportLabels[projectExport.state]}
                  </Badge>
                  <span>Requested {formatExportDate(projectExport.requestedAt)}</span>
                  {projectExport.expiresAt && (
                    <span className="text-muted-foreground">
                      Expires {formatExportDate(projectExport.expiresAt)}
                    </span>
                  )}
                </div>
                {projectExport.state === "ready" && (
                  <a
                    className="text-sm font-medium text-accent-text underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    href={projectExportDownloadPath(projectId, projectExport.id)}
                    download
                  >
                    Download export
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        aria-label="Project memory"
        className="flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border p-4"
      >
        <div className="flex-1">
          <h3 className="text-sm font-medium">Clear indexed memory</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Remove indexed project memory entries. Sources can be added again.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => setConfirmation("clear-memory")}
        >
          Clear project memory
        </Button>
      </section>

      <section
        aria-label="Project deletion"
        className="flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border border-danger/40 p-4"
      >
        <div className="flex-1">
          <h3 className="text-sm font-medium">Delete project</h3>
          {deletion?.state === "requested" ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {canApproveDeletion
                ? `A deletion request from another owner is awaiting your approval${deletion.requestedAt ? ` since ${formatExportDate(deletion.requestedAt)}` : ""}.`
                : deletionRequestedBySelf
                  ? "Your deletion request is awaiting approval from another project owner."
                  : "A deletion request is awaiting approval from a different project owner."}
            </p>
          ) : deletion?.state === "approved" ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Deletion approved. Project data will be purged
              {deletion.purgeAt
                ? ` on ${formatExportDate(deletion.purgeAt)}`
                : " after the retention period"}
              .
            </p>
          ) : deletion?.state === "purged" ? (
            <p className="mt-1 text-sm text-muted-foreground">Project data has been purged.</p>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">
              Deletion cannot be undone. A different owner must approve a new request before project
              data is purged.
            </p>
          )}
        </div>
        {deletion?.state === null && (
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={() => setConfirmation("request-deletion")}
          >
            Request deletion
          </Button>
        )}
        {canApproveDeletion && (
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={() => setConfirmation("approve-deletion")}
          >
            Approve deletion
          </Button>
        )}
      </section>

      <Dialog
        open={confirmation !== null}
        onOpenChange={(open) => {
          if (!pending && !open) setConfirmation(null);
        }}
      >
        <DialogContent>
          <DialogTitle>
            {confirmation === "request-deletion"
              ? "Request project deletion?"
              : confirmation === "approve-deletion"
                ? "Approve project deletion?"
                : confirmation === "clear-memory"
                  ? "Clear project memory?"
                  : confirmation === "confirm-shortening"
                    ? "Confirm retention change?"
                    : "Request shorter retention?"}
          </DialogTitle>
          <DialogDescription>
            {confirmation === "request-deletion" ? (
              <>
                This request cannot be withdrawn. A different project owner must approve it before
                the project is deleted. Data will be purged after the retention period.
              </>
            ) : confirmation === "approve-deletion" ? (
              <>
                You are approving a deletion request made by another owner. This cannot be undone.
                Data will be purged after the retention period.
              </>
            ) : confirmation === "clear-memory" ? (
              <>Indexed project memory will be removed. Sources can be added again.</>
            ) : confirmation === "confirm-shortening" ? (
              <>
                You are confirming a change requested by another owner. Project evidence and Zot
                chat history retention will become{" "}
                {retentionLabels[retentionData.pendingRetention!]}.
              </>
            ) : (
              <>
                This requests a change to {retentionLabels[draft ?? retentionData.retention]}.
                Retention will not shorten until a different project owner confirms it.
              </>
            )}
          </DialogDescription>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => setConfirmation(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={
                confirmation === "request-deletion" ||
                confirmation === "approve-deletion" ||
                confirmation === "clear-memory"
                  ? "destructive"
                  : "default"
              }
              disabled={pending}
              onClick={() => {
                if (confirmation === "request-deletion" || confirmation === "approve-deletion")
                  deleteProject.mutate();
                else if (confirmation === "clear-memory") clearMemory.mutate();
                else confirmShortening();
              }}
            >
              {deleteProject.isPending || clearMemory.isPending
                ? "Requesting…"
                : confirmation === "clear-memory"
                  ? "Clear memory"
                  : confirmation === "confirm-shortening"
                    ? "Confirm retention"
                    : confirmation === "request-deletion" || confirmation === "approve-deletion"
                      ? confirmation === "approve-deletion"
                        ? "Approve deletion"
                        : "Request deletion"
                      : "Request change"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
